import { useSearchParams } from 'next/navigation';
import { useEffect, useRef, useState } from 'react';

export function useDebouncedQuery({
	debounceTimeMs,
	queryMinLength,
	onSubmitQuery,
}: {
	debounceTimeMs: number;
	queryMinLength: number;
	onSubmitQuery: (newQuery: string) => void;
}) {
	const params = useSearchParams();
	const query = params.get('search');

	const debounceTimerRef = useRef<number | null>(null);
	const [pendingQuery, setPendingQuery] = useState(query ?? '');

	const clearDebounce = () => {
		if (debounceTimerRef.current) {
			window.clearTimeout(debounceTimerRef.current);
		}
	};

	// ensure pending query and debounce are cleared when search is closed
	useEffect(() => {
		if (query === null) {
			setPendingQuery('');
			clearDebounce();
		}
	}, [query, setPendingQuery]);

	const updateQuery = (newQuery: string) => {
		setPendingQuery(newQuery);
		clearDebounce();

		// early return when SEARCH_MIN_LENGTH is not met
		if (newQuery.length < queryMinLength) {
			return;
		}

		// start debounce
		debounceTimerRef.current = window.setTimeout(
			() => onSubmitQuery(newQuery),
			debounceTimeMs,
		);
	};

	return {
		query: query ?? '',
		pendingQuery,
		updateQuery,
	};
}
